/**
 * View types for the Stock section.
 *
 * `StockRow`, `Batch`, `PurchaseOrder`, `Supplier` and `ReorderSuggestion` come from
 * `@elutide/shared` and are not restated here. What is here is the extra shape the stock
 * endpoints return alongside them, and the filter state the screens hold.
 */
import type {
  Batch,
  Cents,
  NyDate,
  PoStatus,
  ProductFamily,
  PurchaseOrder,
  ReorderSuggestion,
  StockPageFamily,
  StockPageFamilyPacker,
  StockPageKpis,
  StockPageKpisPacker,
  StockPageResponse,
  StockPageResponsePacker,
  StockPageRow,
  StockPageRowPacker,
  StockRow,
  StockRowDetail,
  StockRowDetailPacker,
  StockStatus,
  StockWacBreakdown,
  StockWacBreakdownPacker,
  Supplier,
  WacLedgerEntry,
} from '@elutide/shared';

/** A packer's row: the same shape with every money field absent, not null. */
export type AnyStockRow = StockRow | Omit<StockRow, 'price_cents' | 'regular_price_cents' | 'wac_cents' | 'wac_source' | 'gross_margin_pct' | 'alt_source_cents'>;

export function hasMoney(row: AnyStockRow): row is StockRow {
  return 'price_cents' in row;
}

/*
 * The Stock Overview as either role receives it.
 *
 * CONTRACTS.md 3.6: a packer's response carries no money, and what is removed is absent, not
 * null. The page renders these unions and asks `rowHasMoney` and friends before it touches a
 * money field, so a packer sees the column, the tile, the drawer section and the CSV column
 * simply not exist, never a dash, never "no data", never a "$".
 */
export type AnyStockPageRow = StockPageRow | StockPageRowPacker;
export type AnyStockPageFamily = StockPageFamily | StockPageFamilyPacker;
export type AnyStockPageKpis = StockPageKpis | StockPageKpisPacker;
export type AnyStockPageResponse = StockPageResponse | StockPageResponsePacker;
export type AnyStockRowDetail = StockRowDetail | StockRowDetailPacker;
export type AnyStockWacBreakdown = StockWacBreakdown | StockWacBreakdownPacker;

export function rowHasMoney(row: AnyStockPageRow): row is StockPageRow {
  return 'price_cents' in row;
}

export function familyHasMoney(family: AnyStockPageFamily): family is StockPageFamily {
  return 'inv_value_cents' in family;
}

export function kpisHaveMoney(kpis: AnyStockPageKpis): kpis is StockPageKpis {
  return 'inventory_at_cost_cents' in kpis;
}

/** True when any size on the page carries money, which is to say the caller is not a packer. */
export function pageHasMoney(families: readonly AnyStockPageFamily[]): boolean {
  return families.some((family) => family.rows.some(rowHasMoney));
}

export function detailHasMoney(detail: AnyStockRowDetail): detail is StockRowDetail {
  return 'price_history' in detail;
}

export function breakdownHasMoney(breakdown: AnyStockWacBreakdown): breakdown is StockWacBreakdown {
  return 'wac_cents' in breakdown;
}

export interface StockSummary {
  visible: number;
  critical: number;
  low: number;
  out: number;
  hidden: number;
  bundles_excluded: number;
  inventory_at_cost?: number;
  inbound_at_cost?: number;
  inbound_units: number;
  next_inbound_eta: NyDate | null;
  days_of_inventory: number | null;
  burn_units_per_day: number;
  negative_stock_skus: number;
}

export interface StockFilters {
  q: string;
  status: StockStatus[];
  family: ProductFamily | '';
  visible: 'all' | 'true' | 'false';
  include_bundles: boolean;
  group_by_family: boolean;
}

export const EMPTY_FILTERS: StockFilters = {
  q: '',
  status: [],
  family: '',
  visible: 'all',
  include_bundles: false,
  group_by_family: true,
};

export interface StockDetail extends StockRow {
  settings: {
    safety_days: number;
    target_cover_days: number;
    pack_size: number;
    min_order_qty: number;
    preferred_supplier_id: string | null;
    reorder_snoozed_until: NyDate | null;
    suppressed_at: string | null;
    notes: string | null;
  };
  batch_count: number;
  last_received_date: NyDate | null;
  /** Woo date_modified_gmt, echoed back as expected_version on a price edit. */
  woo_updated_at: string | null;
}

export interface LastReceipt {
  entry_id: string;
  on_hand_before: number;
  wac_before_cents: number;
  qty_received: number;
  unit_cost_cents: number;
  wac_after_cents: number;
}

export interface LedgerPage {
  /**
   * The API builds these rows from the replay and reads `created_at` from `wac_ledger` by
   * entry id, so a replayed row with no stored counterpart carries a null. `WacLedgerEntry`
   * types it non-null, which is why it is overridden here rather than trusted.
   */
  items: (Omit<WacLedgerEntry, 'created_at'> & {
    created_at: string | null;
    reason: string | null;
  })[];
  next_cursor: string | null;
  total_estimate: number | null;
  last_receipt: LastReceipt | null;
  warnings: string[];
}

export interface BatchRow extends Batch {
  po_number: string | null;
  supplier_name: string | null;
  qty_rejected: number;
  fifo_remaining: number;
  wac_after_cents: number | null;
  coa_present: boolean;
}

export interface HistoryResponse {
  sales: {
    order_id: number;
    order_number: string;
    date_paid: string | null;
    qty: number;
    unit_price_cents: number;
    unit_cost_at_sale_cents: number | null;
    lot_number: string | null;
  }[];
  adjustments: {
    adjustment_id: string;
    delta: number;
    reason: string;
    note: string | null;
    counted_qty: number | null;
    is_cycle_count: boolean;
    approval_state: string;
    actor_user_id: string | null;
    created_at: string | null;
  }[];
  /** One point per batch. This is the cost per unit by supplier series. */
  receipts: {
    batch_id: string;
    lot_number: string | null;
    po_number: string | null;
    supplier_id: string | null;
    supplier_name: string | null;
    qty_received: number;
    received_date: NyDate | null;
    available_date: NyDate | null;
    unit_goods_cents: number;
    landed_unit_cents: number;
    cost_status: string;
  }[];
}

/** The purchase order fields a packer's response leaves out. Absent, never null. */
export type PoMoneyKey =
  | 'currency'
  | 'fx_rate'
  | 'goods_cents'
  | 'freight_cents'
  | 'duties_cents'
  | 'other_cents'
  | 'total_landed_cents'
  | 'paid_to_date_cents';

export interface PoRow extends Omit<PurchaseOrder, PoMoneyKey>, Partial<Pick<PurchaseOrder, PoMoneyKey>> {
  freight_allocation?: 'units' | 'value';
  line_count: number;
  unit_count: number;
  pipeline_step: number;
  supplier_lead_p80: number | null;
}

export function poHasMoney(po: PoRow): po is PoRow & Required<Pick<PurchaseOrder, PoMoneyKey>> {
  return typeof po.total_landed_cents === 'number';
}

export interface PoLineRow {
  po_line_id: string;
  po_id: string;
  product_id: number;
  sku: string | null;
  name: string | null;
  qty_ordered: number;
  qty_received: number;
  qty_rejected: number;
  unit_cost_cents?: number;
  line_total_cents?: number;
  expected_at: NyDate | null;
  notes: string | null;
}

export interface AllocationPreview {
  lines: {
    po_line_id: string;
    product_id: number;
    qty: number;
    goods_cents: number;
    by_category: Record<string, number>;
    allocated_cents: number;
    total_landed_cents: number;
    landed_unit_cents: number;
  }[];
  goods_total_cents: number;
  allocated_total_cents: number;
  total_landed_cents: number;
}

export interface PoDetail extends PoRow {
  lines: PoLineRow[];
  /** Absent for a packer, with `balance_due_cents` and every `*_cents` on the row. */
  allocation?: AllocationPreview;
  batches: {
    batch_id: string;
    product_id: number;
    lot_number: string | null;
    qty_received: number;
    status: string;
    received_date: NyDate | null;
    available_date: NyDate | null;
    landed_unit_cents?: number;
    coa_present: boolean;
  }[];
  balance_due_cents?: number;
  supplier_lead_sample_n: number;
}

export interface PoListResponse {
  items: PoRow[];
  next_cursor: string | null;
  total_estimate: number | null;
  /** The two valuation tiles. Absent for a packer. */
  kpis?: { po_balance_due: number; pending_inbound_value: number };
}

export interface SuggestionRow extends ReorderSuggestion {
  name: string;
  lead_p80_days: number;
  lead_sample_n: number;
  safety_days: number;
  target_cover_days: number;
  pack_size: number;
  covered: boolean;
  /** Absent for a packer. */
  last_unit_cost_cents?: number | null;
  preferred_supplier_name: string | null;
  trending: boolean;
}

/** What `POST /po/suggestions/:productId/snooze` actually returns once the row is hidden. */
export interface SnoozeAck {
  product_id: number;
  snoozed_until: NyDate | null;
}

export interface SupplierRow extends Supplier {
  contact: { name: string | null; channel: string | null; handle: string | null };
  auto_release_on_coa: boolean;
  notes: string | null;
  on_time_pct: number | null;
  open_po_count: number;
  active_batch_count: number;
  last_ordered_at: NyDate | null;
  lead_insufficient_data: boolean;
}

export interface SupplierPriceRow {
  supplier_price_id: string;
  supplier_id: string;
  supplier_name: string;
  product_id: number | null;
  sku: string | null;
  vendor_sku: string | null;
  description: string | null;
  box_price_cents: number | null;
  units_per_box: number | null;
  unit_price_cents: number;
  negotiated_price_cents: number | null;
  effective_unit_cents: number;
  moq: number | null;
  valid_from: NyDate | null;
  valid_to: NyDate | null;
  stale: boolean;
  notes: string | null;
}

export interface CompareResponse {
  product_id: number;
  wac_cents: number | null;
  wac_source: string;
  best_landed_cents: number | null;
  star: boolean;
  rows: {
    supplier_id: string;
    supplier: string;
    unit_price_cents: number;
    landed_estimate_cents: number;
    vs_wac_pct: number | null;
    lead_p80_days: number | null;
    stale: boolean;
    moq: number | null;
  }[];
}

export interface AdjustResult {
  adjustment_id?: string;
  delta: number;
  applied: boolean;
  approval_state: string;
  qty_after?: number;
  reasons?: string[];
  packer_daily_cap?: number;
}

export interface NewPoLineDraft {
  product_id: number;
  sku: string | null;
  name: string;
  qty_ordered: number;
  unit_cost_cents: number;
}

export interface NewPoDraft {
  supplier_id: string;
  expected_at: NyDate | null;
  freight_allocation: 'units' | 'value';
  freight_cents: number;
  testing_cents: number;
  lines: NewPoLineDraft[];
}

export const PO_PIPELINE_LABELS: { status: PoStatus; label: string }[] = [
  { status: 'ordered', label: 'Ordered' },
  { status: 'in_transit', label: 'In transit' },
  { status: 'received', label: 'Received' },
  { status: 'qc', label: 'Processing' },
  { status: 'available', label: 'Available' },
];

export type { Cents };
